//@flow
import React, {useState, useEffect, useMemo} from 'react';
import {
  makeStyles,
  Typography,
  Grid,
  Box,
  Checkbox,
  ListItemText,
} from '@material-ui/core';
import {colors} from '../../../assets/styles/colors';
import {Settings} from '@material-ui/icons';
import {Link} from 'react-router-dom';
import CustomButton from '../../../elements/CustomButton/CustomButton';
import CustomSelect from '../../../elements/CustomSelect/CustomSelect';
import {MenuItem} from '@material-ui/core';
import TrainingOverlay from './TrainingOverlay';
import {useChatBotsActions} from '../hooks';
import {useToastActions} from '../../toast-alert/utils';
import {useStoreDevices} from '../../multichat/chat/utils';

import type {StoreChatbot} from '../../../entities';

const useStyles = makeStyles(theme => ({
  sectionCard: {
    padding: '1rem',
    [theme.breakpoints.down('xs')]: {
      padding: '.5rem',
    },
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    backgroundColor: 'white',
    borderRadius: '12px',
    overflow: 'hidden',
    position: 'relative',
  },
  subtitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: colors.grey800,
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  caption: {
    fontSize: '.875rem',
    color: colors.grey800,
  },
  sectionFooter: {
    display: 'flex',
    justifyContent: 'flex-start',
    marginTop: '1rem',
  },
  submitButton: {
    maxWidth: '200px',
  },
  select: {},
  itemSelectFontColor: {},
}));

type Props = {
  chatBot: StoreChatbot,
  onSaveSuccess: () => void,
};

const DeviceLinkingSection = React.memo(function DeviceLinkingSection({
  chatBot,
  onSaveSuccess,
}: Props) {
  const classes = useStyles();
  const {editChatBot} = useChatBotsActions();
  const {showToast} = useToastActions();
  const {storeDevices, isLoading} = useStoreDevices({refetchOnMount: true});

  const [devicesSelected, setDevicesSelected] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const devicesStoreStateConnected = useMemo(
    () => storeDevices.filter(device => device.state === 'CONNECTED'),
    [storeDevices]
  );

  useEffect(() => {
    if (chatBot?.devices) {
      const devices = chatBot.devices
        .map(d =>
          devicesStoreStateConnected.find(device => device.id === d.deviceId)
        )
        .filter(Boolean);
      setDevicesSelected(devices);
    }
  }, [chatBot, devicesStoreStateConnected]);

  const handleUpdateDevices = async () => {
    if (!chatBot?.id) return;

    try {
      setIsSaving(true);
      await editChatBot(chatBot.id, {
        devices: devicesSelected.map(d => ({
          id: d.id,
          ravenService: d.service,
        })),
      });

      showToast({
        message: 'Dispositivos vinculados correctamente',
        title: 'Cambios guardados',
        type: 'success',
        duration: 3000,
      });

      onSaveSuccess();
    } catch (error) {
      showToast({
        message: 'Error al vincular dispositivos',
        title: 'Error',
        type: 'error',
        duration: 3000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Grid item xs={12}>
        <section className={classes.sectionCard}>
          <TrainingOverlay
            visible={isLoading}
            message="Cargando dispositivos..."
          />
          <header className={classes.sectionHeader}>
            <Typography className={classes.subtitle}>Vincula tu bot</Typography>
          </header>
        </section>
      </Grid>
    );
  }

  return (
    <Grid item xs={12}>
      <section className={classes.sectionCard}>
        <TrainingOverlay
          visible={isSaving}
          message="Guardando vinculación de dispositivos..."
        />
        <header className={classes.sectionHeader}>
          <Typography className={classes.subtitle}>Vincula tu bot</Typography>
        </header>

        <Typography className={classes.caption}>
          Selecciona los canales que deseas vincular con tu bot
        </Typography>

        {devicesStoreStateConnected.length > 0 ? (
          <>
            <CustomSelect
              fullWidth
              className={classes.select}
              multiple
              value={devicesSelected}
              onChange={e => {
                setDevicesSelected(e.target.value);
              }}
              renderValue={selected =>
                selected.map(i => i.identifier).join(', ')
              }
            >
              {devicesStoreStateConnected.map(el => (
                <MenuItem
                  key={el.id}
                  value={el}
                  className={classes.itemSelectFontColor}
                >
                  <Checkbox
                    checked={devicesSelected.some(d => d.id === el.id)}
                    color="primary"
                  />
                  <ListItemText primary={el.identifier} />
                </MenuItem>
              ))}
            </CustomSelect>

            <footer className={classes.sectionFooter}>
              <CustomButton
                variant="contained"
                color="primary"
                className={classes.submitButton}
                onClick={handleUpdateDevices}
                disabled={isSaving}
                fullWidth
              >
                {isSaving ? 'Guardando...' : 'Guardar cambios'}
              </CustomButton>
            </footer>
          </>
        ) : (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            py={4}
            textAlign="center"
            gap={2}
          >
            <Settings style={{fontSize: 48, color: colors.grey500}} />
            <Typography variant="h6" className={classes.subtitle} gutterBottom>
              No hay canales disponibles
            </Typography>
            <Typography
              variant="body2"
              className={classes.caption}
              gutterBottom
            >
              Para vincular tu bot necesitas tener al menos un canal configurado
              y conectado.
            </Typography>
            <CustomButton
              variant="outlined"
              color="primary"
              component={Link}
              to="/chat/channels"
              startIcon={<Settings />}
            >
              Configurar canales
            </CustomButton>
          </Box>
        )}
      </section>
    </Grid>
  );
});

export default DeviceLinkingSection;
